import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy } from "lucide-react";
import Modal from "@/components/modal";
import CheckIcon from "@/assets/images/successfully-check.svg"
import Button from "@/components/buttons/Button";

interface IProps {
    isOpen: boolean;
    onClose: (value: boolean) => void;
    /** Where "Back to Applications" leads. Defaults to the licence list. */
    backTo?: string;
    /** The control number issued for this application, once the API has returned one. */
    controlNumber?: string | null;
    /** True while the bill is still being raised. */
    isIssuing?: boolean;
    /** True when the applicant filed as a draft and asked to pay later. */
    payLater?: boolean;
}

/**
 * Shown once an application has been filed.
 *
 * The control number is issued from the submission itself, so it is reported here rather than
 * promised in advance. When it is not there yet — the applicant chose to pay later, or the
 * request to ZanMalipo has not come back — the card says so plainly instead of leaving the
 * applicant to guess whether one is coming.
 */
export default function SuccessfulApplicationCard({ ...props }:IProps) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        if (!props.controlNumber) return;
        navigator.clipboard.writeText(props.controlNumber);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={props.onClose}
            size="lg"
            className="bg-gradient-to-b shadow-none from-primary-300 w-[500px] to-white"
        >
            <div className="flex flex-col gap-5 pb-4">
                <div>
                    <div className="flex items-center justify-center mb-4">
                        <img src={CheckIcon} alt="Success Icon" />
                    </div>
                    <div className="max-w-[350px] mx-auto">
                        <h2 className="text-2xl font-semibold text-[#041528] mb-2 text-center">
                            Your Application Has Been Successfully Submitted!
                        </h2>
                    </div>
                </div>

                {props.controlNumber ? (
                    <div className="bg-white/70 border border-orange-100 rounded-2xl p-5 text-center">
                        <p className="text-[11px] font-semibold text-orange-400 uppercase tracking-[0.15em] mb-2">
                            Control Number
                        </p>
                        <p className="text-3xl font-bold text-gray-900 tracking-[0.12em] font-mono">
                            {props.controlNumber}
                        </p>
                        <button
                            type="button"
                            onClick={handleCopy}
                            className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-orange-500 hover:text-orange-600 transition-colors"
                        >
                            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                            {copied ? 'Copied!' : 'Copy number'}
                        </button>
                        <p className="text-xs text-[#536067] mt-3">
                            Pay this number from your mobile money account to complete your application.
                        </p>
                    </div>
                ) : (
                    <div className="bg-white/70 border border-gray-100 rounded-2xl p-5 text-center">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-[0.15em] mb-2">
                            Control Number
                        </p>
                        <p className="text-lg font-semibold text-gray-700">
                            {props.isIssuing ? "Issuing your control number…" : "Awaiting control number"}
                        </p>
                        <p className="text-xs text-[#536067] mt-2">
                            {props.isIssuing
                                ? "This takes a moment. It will appear on your application once issued."
                                : props.payLater
                                    ? "Your application is saved as a draft. A control number will be issued when you choose to pay."
                                    : "We have not received a control number yet. It will appear on your application as soon as it is issued."}
                        </p>
                    </div>
                )}

                <div className="flex justify-center">
                    <Link to={props.backTo ?? "/applications/licences"}>
                        <Button
                            intent="default"
                            type="button"
                            className="px-5 py-2.5 border-[#3C1E03] text-[#3C1E03] font-semibold">
                            Back to Applications
                        </Button>
                    </Link>
                </div>
            </div>
        </Modal>
    );
};
